// src/pages/PortfolioPage.jsx
import React, { useEffect, useState, useRef } from "react"; 
import { useParams } from "react-router-dom";
import { getDoc, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import VanillaTilt from "vanilla-tilt";
import "../styles/portfolioPage.css";

gsap.registerPlugin(ScrollTrigger);

export default function PortfolioPage() {
  const { username } = useParams();
  const [data, setData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const fileInputRef = useRef(null);
  const projRef = useRef(null);

  useEffect(() => {
    getDoc(doc(db, "NewPortfolios", username))
      .then(snap => {
        if (!snap.exists()) throw new Error("User not found");
        setData(snap.data());
      })
      .catch(err => console.error(err));
  }, [username]);

  const handleAvatarClick = () => fileInputRef.current.click();

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const storage = getStorage();
    const avatarRef = ref(storage, `avatars/${username}/${file.name}`);
    const uploadTask = uploadBytesResumable(avatarRef, file);
    await new Promise((res, rej) =>
      uploadTask.on('state_changed', null, rej, () => res())
    );
    const url = await getDownloadURL(avatarRef);
    await updateDoc(doc(db, "NewPortfolios", username), { photoURL: url });
    setData(prev => ({ ...prev, photoURL: url }));
  };

  useEffect(() => {
    if (!data) return;

    // Fade-in on scroll
    gsap.utils.toArray(".reveal").forEach(el => {
      gsap.fromTo(el,
        { autoAlpha: 0, y: 50 },
        {
          duration: 1,
          autoAlpha: 1,
          y: 0,
          ease: "power3.out",
          scrollTrigger: { trigger: el, start: "top 80%" }
        }
      );
    });

    // Tilt on projects
    if (projRef.current) {
      VanillaTilt.init(
        projRef.current.querySelectorAll(".project-card"),
        { max: 15, speed: 400, glare: true, "max-glare": 0.3 }
      );
    }
  }, [data]);

  if (!data) return <div className="loading-message">Loading…</div>;

  return (
    <div className="portfolio-container">
      {/* ——— Edit Toggle ——— */}
      <button className="toggle-edit" onClick={() => setEditMode(!editMode)}>
        {editMode ? "Exit Edit Mode" : "Edit Portfolio"}
      </button>

      {/* ——— Hero & Avatar ——— */}
      <div className="profile-header reveal">
        <div className="profile-overlay">
          <h1>{data.name}</h1>
          <p>{data.tagline || data.bio}</p>
        </div>
      </div>
      <div className="avatar-container" onClick={handleAvatarClick}>
        {data.photoURL
          ? <img src={data.photoURL} alt="Profile" className="avatar-img" />
          : <span className="avatar-initial">{data.name.charAt(0)}</span>
        }
        <input
          ref={fileInputRef}
          type="file"
          id="avatarUpload"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleAvatarUpload}
        />
        {!data.photoURL && <div className="avatar-nudge">Tap to upload your profile picture</div>}
      </div>

      {/* ——— About ——— */}
      <section className="about-section reveal" id="about">
        <div className="about-image">
          <img src={data.photoURL || "/images/default-avatar.jpg"} alt="About" />
        </div>
        <div className="about-text">
          <h2>
            About Me
            {editMode && <button className="pencil-icon">✏️</button>}
          </h2>
          <p>{data.aboutLong || data.bio}</p>
        </div>
      </section>

      {/* ——— Certificates & Awards ——— */}
      <section className="certificates-section reveal" id="certificates">
        <h2>
          Certifications & Awards
          {editMode && <button className="pencil-icon">✏️</button>}
        </h2>
        <div className="certificates-grid">
          {data.certificates?.length > 0 ? (
            data.certificates.map(cert => (
              <div key={cert.id} className="certificate-card">
                <img src={cert.imageURL} alt={cert.title} />
                <div className="certificate-info">
                  <h3>{cert.title}</h3>
                  <p>{cert.issuer} • {cert.year}</p>
                </div>
              </div>
            ))
          ) : (
            <div className="placeholder-card">
              <p>No certificates yet.</p>
              <button className="edit-button">+ Add Certificate</button>
            </div>
          )}
        </div>
      </section>

      {/* ——— Skills ——— */}
      {data.skills?.length > 0 && (
        <section className="skills-section reveal" id="skills">
          <h2>
            My Skills
            {editMode && <button className="pencil-icon">✏️</button>}
          </h2>
          {data.skills.map(({ name, level }) => (
            <div key={name} className="skill-bar" style={{ "--level": `${level}%` }}>
              <div className="label">
                <span>{name}</span>
                <span>{level}%</span>
              </div>
              <div className="progress"></div>
            </div>
          ))}
        </section>
      )}

      {/* ——— Gallery / My Work ——— */}
      <section className="gallery-section reveal" id="gallery">
        <h2>
          Gallery
          {editMode && <button className="pencil-icon">✏️</button>}
        </h2>
        <div className="gallery-grid">
          {data.gallery?.length > 0 ? (
            data.gallery.map(item => (
              <a key={item.id} href={item.link} className="gallery-item">
                <img src={item.imageURL} alt={item.title} />
              </a>
            ))
          ) : (
            <div className="placeholder-card">
              <p>No work in the gallery yet.</p>
              <button className="edit-button">+ Add Gallery Item</button>
            </div>
          )}
        </div>
      </section>

      {/* ——— Featured Projects ——— */}
      {data.projects?.length > 0 && (
        <section className="projects-section reveal" id="projects" ref={projRef}>
          <h2>
            Featured Projects
            {editMode && <button className="pencil-icon">✏️</button>}
          </h2>
          <div className="projects-grid">
            {data.projects.map(p => (
              <div key={p.id} className="project-card">
                <img src={p.imageURL} alt={p.title} />
                <div className="project-overlay">
                  <h3>{p.title}</h3>
                  <p>{p.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ——— Testimonials ——— */}
      <section className="testimonials-section reveal" id="testimonials">
        <h2>
          What People Say
          {editMode && <button className="pencil-icon">✏️</button>}
        </h2>
        <div className="testimonials-carousel">
          {data.testimonials?.length > 0 ? (
            data.testimonials.map(t => (
              <blockquote key={t.id}>
                <p>“{t.quote}”</p>
                <cite>— {t.author}, {t.role}</cite>
              </blockquote>
            ))
          ) : (
            <div className="placeholder-card">
              <p>No testimonials yet.</p>
              <button className="edit-button">+ Add Testimonial</button>
            </div>
          )}
        </div>
      </section>

      {/* ——— Contact ——— */}
      <section className="contact-section reveal" id="contact">
        <h2>
          Work With Me
          {editMode && <button className="pencil-icon">✏️</button>}
        </h2>
        <p>{data.callToAction || "Let's create something amazing together."}</p>
        <a href={`mailto:${data.email}`} className="button">Get in Touch</a>
      </section>
    </div>
  );
}
